/**
 * @module ui/pages/api-catalog/ApiCatalogFilterBar
 *
 * The API Catalog toolbar: search + Method / Status / Domain / Credential as
 * fixed-size inline menus, the "More filters" dialog trigger (Last seen date
 * range), a Clear action, and the active filter chips row. Built from the
 * shared `@chatsdet/ui` `FilterToolbar` primitives — mirrors
 * `ExternalTestsCatalogFilterBar.tsx` / `IssuesFilterBar.tsx`.
 */
import React from 'react';
import {
  Button,
  DateRangePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  FilterToolbarSearch,
  MoreFiltersButton,
  ToolbarFilterMenu,
  type FilterToolbarChip,
  type MultiSelectOption,
} from '@chatsdet/ui';
import type { DateRangeValue } from '../../../utils/dateRange';
import { formatDateRangeDisplay } from '../../../utils/dateRange';

export type ApiCatalogFiltersSearchParams = {
  search?: string;
  domains?: string[];
  methods?: string[];
  statuses?: Array<'2xx' | '4xx' | '5xx'>;
  credentialIds?: string[];
  lastSeenStart?: string;
  lastSeenEnd?: string;
  page?: number;
};

const STATUS_LABELS: Record<'2xx' | '4xx' | '5xx', string> = {
  '2xx': '2xx Success',
  '4xx': '4xx Client Error',
  '5xx': '5xx Server Error',
};

const METHOD_OPTIONS: MultiSelectOption[] = [
  { id: 'GET', label: 'GET' },
  { id: 'POST', label: 'POST' },
  { id: 'PUT', label: 'PUT' },
  { id: 'PATCH', label: 'PATCH' },
  { id: 'DELETE', label: 'DELETE' },
];

const STATUS_OPTIONS: MultiSelectOption[] = [
  { id: '2xx', label: STATUS_LABELS['2xx'] },
  { id: '4xx', label: STATUS_LABELS['4xx'] },
  { id: '5xx', label: STATUS_LABELS['5xx'] },
];

type ApiCatalogFilterBarProps = {
  searchInput: string;
  onSearchChange: (value: string) => void;

  domainFilter: string[];
  methodFilter: string[];
  statusFilter: Array<'2xx' | '4xx' | '5xx'>;
  credentialFilter: string[];
  lastSeenRange: DateRangeValue;

  domainOptions: MultiSelectOption[];
  credentialOptions: MultiSelectOption[];
  domainsLoading: boolean;
  credentialsLoading: boolean;

  /** Single source of truth for "any filter active", computed by the page. */
  hasActiveFilters: boolean;

  onFilterChange: (updates: Partial<ApiCatalogFiltersSearchParams>) => void;
  onLastSeenChange: (range: DateRangeValue) => void;
  onClearLastSeen: () => void;

  isMoreFiltersOpen: boolean;
  onMoreFiltersOpenChange: (open: boolean) => void;
};

export function ApiCatalogFilterBar({
  searchInput,
  onSearchChange,
  domainFilter,
  methodFilter,
  statusFilter,
  credentialFilter,
  lastSeenRange,
  domainOptions,
  credentialOptions,
  domainsLoading,
  credentialsLoading,
  hasActiveFilters,
  onFilterChange,
  onLastSeenChange,
  onClearLastSeen,
  isMoreFiltersOpen,
  onMoreFiltersOpenChange,
}: ApiCatalogFilterBarProps) {
  const isDateRangeInvalid = React.useMemo(() => {
    if (!lastSeenRange?.start || !lastSeenRange?.end) return false;
    return lastSeenRange.start.compare(lastSeenRange.end) > 0;
  }, [lastSeenRange]);

  const lastSeenLabel = formatDateRangeDisplay(lastSeenRange);

  const activeFilterChips: FilterToolbarChip[] = React.useMemo(() => {
    const chips: FilterToolbarChip[] = [];
    domainFilter.forEach((domain) =>
      chips.push({ id: `domain:${domain}`, label: `Domain: ${domain}` })
    );
    methodFilter.forEach((method) =>
      chips.push({ id: `method:${method}`, label: `Method: ${method}` })
    );
    statusFilter.forEach((status) =>
      chips.push({ id: `status:${status}`, label: `Status: ${STATUS_LABELS[status]}` })
    );
    if (lastSeenLabel) chips.push({ id: 'lastSeen', label: `Last seen: ${lastSeenLabel}` });
    credentialFilter.forEach((credId) => {
      const label = credentialOptions.find((c) => c.id === credId)?.label ?? credId;
      chips.push({ id: `credential:${credId}`, label: `Credential: ${label}` });
    });
    return chips;
  }, [credentialFilter, credentialOptions, domainFilter, lastSeenLabel, methodFilter, statusFilter]);

  const showClearAll = hasActiveFilters || searchInput.trim().length > 0;

  const handleChipRemove = React.useCallback(
    (chipId: string) => {
      if (chipId === 'lastSeen') {
        onClearLastSeen();
        return;
      }
      if (chipId.startsWith('domain:')) {
        const domain = chipId.slice('domain:'.length);
        const next = domainFilter.filter((d) => d !== domain);
        onFilterChange({ domains: next.length > 0 ? next : undefined, page: 1 });
        return;
      }
      if (chipId.startsWith('method:')) {
        const method = chipId.slice('method:'.length);
        const next = methodFilter.filter((m) => m !== method);
        onFilterChange({ methods: next.length > 0 ? next : undefined, page: 1 });
        return;
      }
      if (chipId.startsWith('status:')) {
        const status = chipId.slice('status:'.length) as '2xx' | '4xx' | '5xx';
        const next = statusFilter.filter((s) => s !== status);
        onFilterChange({ statuses: next.length > 0 ? next : undefined, page: 1 });
        return;
      }
      if (chipId.startsWith('credential:')) {
        const credId = chipId.slice('credential:'.length);
        const next = credentialFilter.filter((id) => id !== credId);
        onFilterChange({ credentialIds: next.length > 0 ? next : undefined, page: 1 });
        return;
      }
    },
    [credentialFilter, domainFilter, methodFilter, onClearLastSeen, onFilterChange, statusFilter]
  );

  const handleClearAll = React.useCallback(() => {
    onSearchChange('');
    onFilterChange({
      search: undefined,
      domains: undefined,
      methods: undefined,
      statuses: undefined,
      credentialIds: undefined,
      lastSeenStart: undefined,
      lastSeenEnd: undefined,
      page: 1,
    });
  }, [onFilterChange, onSearchChange]);

  const modalFiltersCount = lastSeenLabel ? 1 : 0;

  return (
    <FilterToolbar>
      <FilterToolbarSearch
        aria-label="Search API requests"
        placeholder="Search by path or domain"
        value={searchInput}
        onChange={onSearchChange}
      />
      <ToolbarFilterMenu
        label="Method"
        options={METHOD_OPTIONS}
        selectedIds={methodFilter}
        onChange={(ids) => onFilterChange({ methods: ids.length > 0 ? ids : undefined, page: 1 })}
        searchable={false}
      />
      <ToolbarFilterMenu
        label="Status"
        options={STATUS_OPTIONS}
        selectedIds={statusFilter}
        onChange={(ids) =>
          onFilterChange({
            statuses: ids.length > 0 ? (ids as Array<'2xx' | '4xx' | '5xx'>) : undefined,
            page: 1,
          })
        }
        searchable={false}
      />
      <ToolbarFilterMenu
        label="Domain"
        options={domainOptions}
        selectedIds={domainFilter}
        onChange={(ids) => onFilterChange({ domains: ids.length > 0 ? ids : undefined, page: 1 })}
        isLoading={domainsLoading}
      />
      <ToolbarFilterMenu
        label="Credential"
        options={credentialOptions}
        selectedIds={credentialFilter}
        onChange={(ids) =>
          onFilterChange({ credentialIds: ids.length > 0 ? ids : undefined, page: 1 })
        }
        isLoading={credentialsLoading}
      />

      <FilterToolbarDivider />

      <DialogTrigger isOpen={isMoreFiltersOpen} onOpenChange={onMoreFiltersOpenChange} size="md">
        <MoreFiltersButton count={modalFiltersCount} />
        <Dialog title="More filters" description="Narrow down requests by when they were last seen.">
          <DialogContent className="grid gap-4">
            <DateRangePicker
              label="Last seen"
              value={lastSeenRange}
              onChange={onLastSeenChange}
              granularity="day"
            />
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={onClearLastSeen}>
              Reset
            </Button>
            <Button
              variant="primary"
              onPress={() => onMoreFiltersOpenChange(false)}
              isDisabled={isDateRangeInvalid}
            >
              Done
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {showClearAll && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onPress={handleClearAll}>
          Clear
        </Button>
      )}

      <FilterToolbarChips chips={activeFilterChips} onRemove={handleChipRemove} />
    </FilterToolbar>
  );
}
